// The DME order workflow, in one place.
//
// Every DME page used to carry its own copy of the status list and its own
// "Advance →" that moved an order one step with no questions asked. This module
// owns the pipeline, says what each transition needs before it may happen, and
// performs the transition with an audit row. Pages render what it returns.
//
// The gates come from what a payer will actually ask for: Georgia's
// medical-necessity review (signed order ≤ 12 months, diagnosis, frequency,
// quantity within the monthly allowable, age within the EPSDT band), a real
// authorization, a CMN where the item needs one, and a proof of delivery that
// meets the CMS standard (who received it, when, how many, signed by someone
// with no financial interest in the delivery).

import { differenceInMonths, differenceInYears, addMonths, format, parseISO } from 'date-fns';
import { supabase } from './supabase';

export const STATUS_PIPELINE = [
  { key: 'referral_received', label: 'Referral Received', short: 'Referral',  color: 'var(--text3)',   bg: 'var(--bg2)' },
  { key: 'auth_pending',      label: 'Auth Pending',      short: 'Auth ⏳',    color: 'var(--warn)',    bg: 'var(--warn-bg)' },
  { key: 'auth_approved',     label: 'Auth Approved',     short: 'Auth ✓',    color: 'var(--success)', bg: 'var(--success-bg)' },
  { key: 'cmn_pending',       label: 'CMN Pending',       short: 'CMN ⏳',     color: 'var(--warn)',    bg: 'var(--warn-bg)' },
  { key: 'cmn_complete',      label: 'CMN Complete',      short: 'CMN ✓',     color: 'var(--success)', bg: 'var(--success-bg)' },
  { key: 'supplier_ordered',  label: 'Supplier Ordered',  short: 'Ordered',   color: 'var(--info)',    bg: 'var(--info-bg)' },
  { key: 'shipped',           label: 'Shipped',           short: 'Shipped',   color: 'var(--info)',    bg: 'var(--info-bg)' },
  { key: 'delivered',         label: 'Delivered',         short: 'Delivered', color: 'var(--info)',    bg: 'var(--info-bg)' },
  { key: 'pod_confirmed',     label: 'POD Confirmed',     short: 'POD ✓',     color: 'var(--success)', bg: 'var(--success-bg)' },
  { key: 'claim_submitted',   label: 'Claim Submitted',   short: 'Claimed',   color: 'var(--warn)',    bg: 'var(--warn-bg)' },
  { key: 'claim_paid',        label: 'Claim Paid',        short: 'Paid ✓',    color: 'var(--success)', bg: 'var(--success-bg)' },
];

export const CATEGORIES = [
  { key: 'incontinence', label: 'Incontinence' },
  { key: 'ostomy',       label: 'Ostomy' },
  { key: 'catheter',     label: 'Catheter / Urological' },
];

export const POD_METHODS = [
  { key: 'direct',   label: 'Delivered in person',        hint: 'Signed delivery slip required' },
  { key: 'shipped',  label: 'Shipped by carrier',         hint: 'Carrier tracking plus delivery date serves as proof' },
  { key: 'facility', label: 'Delivered to a facility',    hint: 'Facility receipt required' },
];

// Off the main line. A denied claim sits beside Claim Submitted rather than
// after it: the way forward is to correct and resubmit, which is the same
// gate as the first submission.
export const SIDE_STATUSES = [
  { key: 'claim_denied', label: 'Claim Denied', short: 'Denied', color: 'var(--danger)', bg: 'var(--danger-bg)', besides: 'claim_submitted' },
];

// Every DME claim goes to CareSource. AHFC's DME line bills the CMO only and
// never DCH/GAMMIS — there is no DME 837P, and nothing here may ever feed the
// GAMMIS transport. Filing happens on CareSource's side; the order records
// the claim number, the amount, and what came back.
export { DME_PAYER } from './serviceModel';

export const statusInfo = (key) =>
  STATUS_PIPELINE.find(s => s.key === key) || SIDE_STATUSES.find(s => s.key === key) || STATUS_PIPELINE[0];
export const statusIndex = (key) => {
  const side = SIDE_STATUSES.find(s => s.key === key);
  return STATUS_PIPELINE.findIndex(s => s.key === (side ? side.besides : key));
};
export const nextStatus = (current) => {
  // Resubmission: the denied claim goes back through the submission gate.
  if (current === 'claim_denied') return 'claim_submitted';
  const i = statusIndex(current);
  return i >= 0 && i < STATUS_PIPELINE.length - 1 ? STATUS_PIPELINE[i + 1].key : null;
};
/** Stages an order may be moved to from `current` that are not the next one. */
export const sideTargets = (current) => SIDE_STATUSES.filter(s => s.besides === current).map(s => s.key);

const today = () => format(new Date(), 'yyyy-MM-dd');
const toDate = (d) => (d ? (d instanceof Date ? d : parseISO(String(d))) : null);
const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';
const isNpi = (v) => /^\d{10}$/.test(String(v || '').trim());
const isIcd10 = (v) => /^[A-TV-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/i.test(String(v || '').trim());

export const ageOn = (dob, on = new Date()) => (dob ? differenceInYears(toDate(on), toDate(dob)) : null);

export const expectedAmount = (feeItem, qty) =>
  feeItem && qty ? Math.round(Number(feeItem.rate) * Number(qty) * 100) / 100 : null;

export const money = (n) => (n === null || n === undefined || n === '' ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n)));

// ---------------------------------------------------------------------------
// Coverage — things a payer would reject, checked against the fee schedule.
// `blocking` stops the order leaving Referral; `warnings` are shown and logged.
// ---------------------------------------------------------------------------
export function coverageCheck(order, client, feeItem) {
  const blocking = [];
  const warnings = [];
  if (!feeItem) {
    blocking.push(`${order.hcpcs_code || 'This item'} is not on the fee schedule. Add it under DME → Items & Charges first.`);
    return { blocking, warnings };
  }
  const age = ageOn(client?.date_of_birth);
  if (age !== null && feeItem.min_age !== null && age < feeItem.min_age)
    blocking.push(`Client is ${age}; ${feeItem.hcpcs_code} is covered from age ${feeItem.min_age}.`);
  if (age !== null && feeItem.max_age !== null && age > feeItem.max_age)
    blocking.push(`Client is ${age}; ${feeItem.hcpcs_code} is covered to age ${feeItem.max_age} (EPSDT).`);
  if (age === null && (feeItem.min_age !== null || feeItem.max_age !== null))
    warnings.push('No date of birth on the client record — age eligibility could not be checked.');
  if (feeItem.allowable_qty && Number(order.quantity_ordered) > feeItem.allowable_qty)
    warnings.push(`${order.quantity_ordered} exceeds the ${feeItem.allowable_qty}/${feeItem.allowable_period} allowable for ${feeItem.hcpcs_code}. Prior authorization must document why.`);
  if (order.rx_date && differenceInMonths(new Date(), toDate(order.rx_date)) >= 12)
    blocking.push(`Prescription dated ${order.rx_date} is over 12 months old. A new signed order is needed.`);
  return { blocking, warnings };
}

// ---------------------------------------------------------------------------
// Requirements — what must be on the order before it may enter `target`.
// Each field: { key, label, type, required, value, hint, options?, docType? }
// `type` is one of text | number | date | select | supplier | cmn | file.
// ---------------------------------------------------------------------------
export function requirementsFor(target, order, ctx = {}) {
  const { feeItem, client, suppliers = [], cmns = [] } = ctx;
  const o = order || {};
  const f = (key, label, type, opts = {}) => ({
    key, label, type, required: true, value: o[key] ?? '', ...opts,
    missing: opts.required === false ? false : isBlank(opts.value ?? o[key]),
  });
  const blocking = [];
  const fields = [];
  let note = '';

  switch (target) {
    case 'auth_pending': {
      // Leaving Referral means the order is complete enough to bill.
      fields.push(
        f('prescribing_physician', 'Prescribing physician / NP / PA', 'text'),
        f('physician_npi', 'Prescriber NPI', 'text', { hint: '10 digits' }),
        f('qualifying_diagnosis', 'Qualifying diagnosis', 'text'),
        f('icd10_code', 'ICD-10 code', 'text', { hint: 'e.g. G80.9' }),
        f('frequency_per_day', 'Uses per day (as ordered)', 'number', { hint: 'Georgia requires the ordered frequency' }),
        f('rx_date', 'Prescription / LMN date', 'date', { hint: 'Must be within the last 12 months' }),
        f('rx_document_url', 'Signed prescription or letter of medical necessity', 'file', { docType: 'DME Prescription' }),
      );
      if (!isBlank(o.physician_npi) && !isNpi(o.physician_npi)) blocking.push('NPI must be exactly 10 digits.');
      if (!isBlank(o.icd10_code) && !isIcd10(o.icd10_code)) blocking.push(`"${o.icd10_code}" is not a valid ICD-10 code.`);
      const cov = coverageCheck(o, client, feeItem);
      blocking.push(...cov.blocking);
      note = cov.warnings.join(' ');
      break;
    }
    case 'auth_approved':
      fields.push(
        f('auth_number', 'Authorization number', 'text'),
        f('auth_start_date', 'Authorization start', 'date'),
        f('auth_end_date', 'Authorization end', 'date', { hint: 'Georgia authorizes at most 6 months at a time' }),
      );
      if (o.auth_start_date && o.auth_end_date) {
        if (toDate(o.auth_end_date) < toDate(o.auth_start_date)) blocking.push('Authorization end is before its start.');
        else if (differenceInMonths(toDate(o.auth_end_date), toDate(o.auth_start_date)) > 6)
          note = 'This authorization runs longer than 6 months — confirm that is what the payer issued.';
      }
      break;
    case 'cmn_pending':
      break;
    case 'cmn_complete': {
      const needsCmn = feeItem ? !!feeItem.cmn_required : !!o.cmn_required;
      if (!needsCmn) { note = `No CMN is required for ${o.hcpcs_code}.`; break; }
      fields.push(f('cmn_id', 'Certificate of Medical Necessity', 'cmn', {
        options: cmns, hint: 'A signed CMN with the document attached',
      }));
      const cmn = cmns.find(c => c.id === o.cmn_id);
      if (o.cmn_id && cmn && (cmn.status !== 'signed' || !cmn.document_url))
        blocking.push('The linked CMN is not marked signed with a document attached. Finish it under CMN Tracking first.');
      break;
    }
    case 'supplier_ordered':
      fields.push(f('supplier_id', 'Supplier', 'supplier', { options: suppliers }));
      if (suppliers.length === 0) blocking.push('No active suppliers. Add one under DME → Suppliers first.');
      break;
    case 'shipped':
      fields.push(
        f('tracking_number', 'Carrier tracking number', 'text'),
        f('shipped_date', 'Shipped on', 'date', { value: o.shipped_date || today() }),
      );
      break;
    case 'delivered':
      fields.push(f('delivered_date', 'Delivered on', 'date', { value: o.delivered_date || today() }));
      break;
    case 'pod_confirmed': {
      const method = o.pod_method || (o.tracking_number ? 'shipped' : 'direct');
      fields.push(
        f('pod_method', 'How was it delivered?', 'select', { options: POD_METHODS, value: method }),
        f('pod_received_by', 'Received by (name)', 'text', { hint: 'Not AHFC staff — the family, patient or facility' }),
        f('pod_delivered_date', 'Date received', 'date', { value: o.pod_delivered_date || o.delivered_date || today() }),
        f('quantity_delivered', 'Quantity received', 'number', { value: o.quantity_delivered ?? o.quantity_ordered ?? '' }),
        f('pod_document_url', 'Signed delivery slip / facility receipt', 'file', {
          docType: 'DME Proof of Delivery',
          required: method !== 'shipped',
          hint: method === 'shipped' ? 'Optional when shipped — tracking + delivery date is the proof' : 'Required',
        }),
      );
      if (method === 'shipped' && isBlank(o.tracking_number)) blocking.push('Shipped deliveries need the carrier tracking number as proof.');
      if (!isBlank(o.quantity_delivered) && !isBlank(o.quantity_ordered) && Number(o.quantity_delivered) > Number(o.quantity_ordered))
        blocking.push('Quantity received is more than was ordered.');
      break;
    }
    case 'claim_submitted':
      fields.push(
        f('claim_number', 'Claim number / ICN', 'text'),
        f('claim_amount', 'Billed amount', 'number', { value: o.claim_amount ?? o.expected_amount ?? '', hint: o.expected_amount ? `Fee schedule: ${money(o.expected_amount)}` : '' }),
        f('claim_submitted_date', 'Submitted on', 'date', { value: o.claim_submitted_date || today() }),
      );
      break;
    case 'claim_paid':
      fields.push(
        f('paid_amount', 'Amount paid', 'number', { value: o.paid_amount ?? o.claim_amount ?? '' }),
        f('claim_paid_date', 'Paid on', 'date', { value: o.claim_paid_date || today() }),
      );
      break;
    case 'claim_denied':
      // Free text on purpose: CareSource's denial wording is not the GAMMIS
      // EOB dictionary, and whether it arrives as a code or a sentence is
      // not yet known. Whatever the coordinator has goes here as given.
      fields.push(
        f('claim_denial_reason', 'Reason given by CareSource', 'text', { hint: 'Copy it as written on the remittance or portal' }),
        f('claim_denied_date', 'Denied on', 'date', { value: o.claim_denied_date || today() }),
      );
      break;
    default:
      break;
  }
  // Recompute `missing` against any defaulted value.
  for (const fld of fields) fld.missing = fld.required && isBlank(fld.value);
  return { fields, blocking, note, canAdvance: blocking.length === 0 && fields.every(x => !x.missing) };
}

// ---------------------------------------------------------------------------
// The transition itself. `patch` holds the values collected for the gate.
// ---------------------------------------------------------------------------
export async function advanceOrder({ order, target, patch = {}, profile, note }) {
  const update = { ...patch, status: target, updated_at: new Date().toISOString() };
  // A delivered recurring order is due again one period after receipt.
  if ((target === 'pod_confirmed' || target === 'delivered') && order.is_recurring) {
    const received = update.pod_delivered_date || update.delivered_date || order.delivered_date || today();
    const months = order.recurring_frequency === 'quarterly' ? 3 : 1;
    update.next_resupply_date = format(addMonths(toDate(received), months), 'yyyy-MM-dd');
    update.resupply_blocked_reason = null;
  }
  if (target === 'pod_confirmed') update.pod_confirmed_at = new Date().toISOString();

  const { data, error } = await supabase.from('dme_orders').update(update).eq('id', order.id).select().single();
  if (error) return { error };
  await supabase.from('dme_order_events').insert({
    order_id: order.id, from_status: order.status, to_status: target,
    note: note || null, performed_by_id: profile?.id || null, performed_by_name: profile?.full_name || null,
  });
  return { data };
}

export async function logOrderEvent(orderId, note, profile) {
  return supabase.from('dme_order_events').insert({
    order_id: orderId, note, performed_by_id: profile?.id || null, performed_by_name: profile?.full_name || null,
  });
}

// ---------------------------------------------------------------------------
// Fee schedule access
// ---------------------------------------------------------------------------
export async function loadFeeSchedule({ activeOnly = true } = {}) {
  let q = supabase.from('dme_fee_schedule').select('*').order('category').order('hcpcs_code');
  if (activeOnly) q = q.eq('is_active', true);
  const { data } = await q;
  return data || [];
}
export const feeItemFor = (schedule, code, payerKey = 'medicaid') =>
  schedule.find(x => x.hcpcs_code === code && x.payer_key === payerKey) || schedule.find(x => x.hcpcs_code === code) || null;
